<template lang="pug">
div
  .specification-editable(v-if="editable")
    .loading-wrapper(v-show="selectsLoading.length > 0")
      q-spinner.loading(color="primary", size="3em")
    .specification-editable-wrapper(v-show="selectsLoading.length === 0")
      .specification-editable-item(v-for="item in specificationData") 
        component.specification-editable-item-select(
          v-if="item.type === 'bind' || item.type === 'dropdown'",
          :is="fields['select']",
          :initialValue="item.select_value",
          :multiple="item.multiple ?? false",
          :label="item.label",
          :fullOptionsPath="getSelectOptionsPath(item.options_path)",
          :optionsParams="item.options_params",
          @initial-load="setSelectsLoading(item.name)",
          @change="val => updateField(item.id, val)",
          @reset="resetSelectValue(item.id)"
        )
        component.specification-editable-item-file(
          v-else-if="item.type === 'attachments'",
          :is="fields['file']",
          :label="item.label",
          :loadedFiles="item.value",
          :multiple="true",
          :name="item.name",
          @change="val => updateFileField(item.id, val)"
        )
        component.specification-editable-item-datetime(
          v-else-if="item.type === 'date'",
          :is="fields['date']",
          :label="item.label",
          :value="getDateValue(item.value)",
          :itsFormField="true",
          @change="val => updateDateField(item.id, val)"
        )
        component.specification-editable-item-number(
          v-else-if="item.type === 'integer'",
          :is="fields['float']",
          :label="item.label",
          :initialValue="item.value ?? ''",
          @change="val => updateField(item.id, val)"
        )
        component.specification-editable-item-boolean(
          v-else-if="item.type === 'boolean'",
          :is="fields['boolean']",
          :label="item.label",
          :initialValue="getBooleanValue(item.value) ?? null",
          @change="val => updateField(item.id, val)"
        )
        component.specification-editable-item-string(
          v-else-if="item.type === 'string' || item.type === 'link'",
          :is="fields['string']",
          :label="item.label",
          :initialValue="item.value ?? ''",
          @change="val => updateField(item.id, val)"
        )

      .button-group.row.justify-center(v-if="editable && specificationData.length > 0")
        q-btn.tech-map-form-submit(flat, no-caps, :label="buttonLabel", @click="onSubmit()")
  table.specification-table.equipment__tech-map(v-else)
    tbody
      tr(v-for="item in specificationData")
        td {{ item.label }}
        td(v-if="item.type === 'attachments'") 
          a.field-value-attachment(
            v-for="attachment in item.value",
            :key="`${attachment.file_name} ${attachment.file_size}`",
            target="_blank",
            :href="attachment.url"
          ) {{ cutFileName(attachment.file_name) }}
        td(v-else-if="item.type === 'date'") {{ getItemDate(item.value) }}
        td(v-else-if="item.type === 'list'")
          div(v-for="(listItems, index) in item.value", :key="`${listItems} ${index}`") {{ listItems }}
        td(v-else-if="item.type === 'link'") 
          a(v-if="item.value", :href="item.value", target="_blank") {{ item.value ? item.value : "" }}
        td(v-else) {{ item.value }}
</template>

<script setup>
import { format, parseISO, parse } from "date-fns";
import { ref, computed, watch, onMounted } from "vue";
import fields from "../../../../../../shared/general_components/fields/index";
import { useStore } from "@/store";
import { el } from "date-fns/locale";

const props = defineProps({
  /* Дата дин. полей, флаг редактирования */
  parentData: { type: Object, default: () => {} },
});

const emit = defineEmits([
  /* Обновление поля спецификаии при редактировании. Принимает объект с id и значением поля */
  "update-field",
  /* Обновление поля типа файл в спецификации. Принимает объект с id и зачением поля */
  "update-file-field",
  /* Сабмит фомры и запуск запроса на обновление дин полей. Не принимает параметров */
  "submit-dynamic-fields",
]);

const store = useStore();

const selectsLoading = ref([]);

const specificationData = computed(() => props.parentData.data);
const editable = computed(() => props.parentData.editable);
const buttonLabel = computed(() => props.parentData.buttonLabel);

const cutFileName = name => {
  const fileType = name.match(/\.(\w+)$/)[0];
  return name?.length > 15 ? `${name.substring(0, 13)}..${fileType}` : name;
};

const getDateValue = date => {
  if (date) {
    return format(parseISO(date), "dd.MM.yyyy");
  } else {
    return date;
  }
};

const getBooleanValue = val => {
  if (val === "Да") return true;
  if (val === "Нет") return false;
  return null;
};

const getSelectOptionsPath = path => {
  return store.state.paths[path];
};

const getItemDate = date => {
  if (!date) {
    return "";
  } else {
    return format(parseISO(date), "dd.MM.yyyy");
  }
};

const setSelectsLoading = name => {
  selectsLoading.value = selectsLoading.value.filter(item => item !== name);
};

const updateDateField = (id, val) => {
  const date = parse(val, "dd.MM.yyyy", new Date());
  if (date instanceof Date && !isNaN(date.getTime())) {
    emit("update-field", { id: id, value: date.toISOString() });
  } else {
    emit("update-field", { id: id, value: val });
  }
};

const resetSelectValue = id => {
  emit("reset-select-value", id);
};

const updateField = (id, value) => {
  let val;

  if (value?.value === null) {
    val = null;
  } else {
    val = value?.value ?? value;
  }

  emit("update-field", { id: id, value: val });
};

const updateFileField = (id, value) => {
  const files = value?.value ?? value;

  if (Array.isArray(files) ? files.length > 0 : Boolean(files)) {
    emit("update-file-field", { id: id, value: files });
  } else {
    emit("update-file-field", { id: id, value: "" });
  }
};

const onSubmit = () => {
  emit("submit-dynamic-fields");
};

watch(
  () => editable.value,
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue === true) {
      selectsLoading.value = specificationData.value
        .filter(item => item.type === "dropdown" || item.type === "bind")
        .map(item => item.name);
    }
  },
  { deep: true },
);

onMounted(() => {
  specificationData.value.map(item => {
    if (item.type === "string" || item.type === "link" || item.type === "integer" || item.type === "date") {
      updateField(item.id, item.value);
    }
    if (item.type === "bind" || item.type === "dropdown") {
      updateField(item.id, item.select_value);
    }
    if (item.type === "attachments") {
      updateField(item.id, "");
    }
  });
});
</script>

<style lang="scss">
.specification-editable {
  .loading-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 400px;
    width: 100%;
  }
}
.specification-table.equipment__tech-map {
  td a {
    word-wrap: break-word;
    display: block;
  }
}
</style>

<style lang="scss" scoped>
.tech-map-form-submit {
  margin-top: 20px;
}
</style>
